<template>
  <div>
    <v-form ref="form" v-model="valid">
      <access-permission-service-select
        v-if="locationPanel === AccessPermissionLocationPanel.PERMITTED"
        v-model:service-name="localValue.serviceName"
      />

      <v-divider v-if="locationPanel === AccessPermissionLocationPanel.PERMITTED"></v-divider>

      <access-permission-restriction-section v-model="localValue" :location-panel="locationPanel" />

      <v-divider></v-divider>

      <access-permission-assign-users v-model="localValue" />
    </v-form>
  </div>
</template>

<script lang="ts">
import cloneDeep from "lodash/cloneDeep";
import defaultsDeep from "lodash/defaultsDeep";
import { defineComponent, type PropType, ref, watch } from "vue";
import {
  AccessPermissionLocationPanel,
  AccessPermissionType,
  Service,
  ServiceStatus,
} from "@/constants/cloud-apps";
import type {
  AccessPermissionItem,
  ServiceSelection,
} from "@/_store/cloud-apps/access-permissions.module";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import type { VuetifyFormRef } from "@/types";
import { useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module";
import { RouteName } from "@/constants/routes.ts";
import { serviceHasMissingRequiredPermissions } from "@/_helpers/cloud-security";
import AccessPermissionServiceSelect from "@/components/modals/access-permissions/AccessPermissionServiceSelect.vue";
import AccessPermissionRestrictionSection from "@/components/modals/access-permissions/AccessPermissionRestrictionSection.vue";
import AccessPermissionAssignUsers from "@/components/modals/access-permissions/AccessPermissionAssignUsers.vue";

const getDefaultRestriction = (
  serviceName: ServiceSelection,
  userType: AccessPermissionType,
  locationPanel: AccessPermissionLocationPanel
) => {
  return {
    serviceName: serviceName,
    userType,
    groups: [],
    labels: [],
    type: locationPanel === AccessPermissionLocationPanel.RESTRICTED ? "ip" : null,
    allowedLocations: [],
    allowedIps: [],
    allowedDays: [],
    allowedStartTime: "",
    allowedEndTime: "",
    remediation: "none",
    locationPanel,
  };
};

export default defineComponent({
  components: {
    AccessPermissionAssignUsers,
    AccessPermissionRestrictionSection,
    AccessPermissionServiceSelect,
  },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<AccessPermissionItem>>,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const cloudAppsStore = useCloudAppsStore();

    const valid = ref(true);
    const form = ref<VuetifyFormRef>();
    const locationPanel =
      props.config.item.locationPanel ?? AccessPermissionLocationPanel.PERMITTED;
    const localValue = ref<AccessPermissionItem>(
      cloneDeep(
        defaultsDeep(
          props.config.item,
          getDefaultRestriction(
            props.config.item.serviceName,
            props.config.item.userType || AccessPermissionType.ALL_USERS,
            locationPanel
          )
        )
      )
    );

    const serviceHasIssues = (serviceName: Service) => {
      const serviceObj = cloudAppsStore.services.find((svc) => svc.name === serviceName)!;

      const criticalStatuses = [ServiceStatus.INCOMPLETE, ServiceStatus.DISCONNECTED];

      if (criticalStatuses.includes(serviceObj.serviceStatus)) {
        return true;
      }

      const isO365MissingAuditLogs =
        serviceObj.name === Service.OFFICE_365 && !serviceObj.auditLogEnabled;

      if (isO365MissingAuditLogs) {
        return true;
      }

      if (serviceObj.permissions?.length) {
        return serviceHasMissingRequiredPermissions(
          serviceObj,
          RouteName.CLOUD_SECURITY_PERMISSIONS_LIST_TAB
        );
      }

      return false;
    };

    const validate = async () => {
      const validationResult = await form.value?.validate();
      return validationResult?.valid;
    };

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true, immediate: true }
    );

    watch(valid, (value) => {
      if (value === null) {
        return;
      }

      emit("update:valid", value);
    });

    watch(
      () => localValue.value.serviceName,
      () => {
        localValue.value.groups = [];
        localValue.value.users = [];
      }
    );

    return {
      AccessPermissionLocationPanel,
      form,
      locationPanel,
      localValue,
      valid,
      serviceHasIssues,
      validate,
    };
  },
});
</script>

<style lang="scss" scoped>
:deep(.restriction-select) {
  width: 252px;
}

:deep(*) {
  .v-select__selection {
    width: 100%;
  }

  .v-field .v-chip {
    --v-chip-height: 32px;
  }
  .toggle-btn {
    .v-btn__content {
      text-transform: capitalize !important;
      letter-spacing: normal !important;
      font-size: 16px !important;
      font-weight: normal !important;
    }
    &:not(.active-btn) {
      border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
    }
  }

  .v-btn.v-btn--active {
    background-color: rgb(var(--v-theme-white)) !important;
    font-weight: 600;

    .v-btn__overlay {
      opacity: 0 !important;
    }
  }
}

:deep(.v-btn-group > .v-btn) {
  text-transform: capitalize !important;
}

:deep(.frequency-button) {
  font-weight: normal;
  letter-spacing: normal;
}

:deep(.time-select) {
  max-width: 349px;
}
</style>
